import { useEffect, useState } from "react";
import { CloudDownload, Download, Smartphone, Trash2, Check, Heart, ListMusic, RefreshCw, Loader2, Wifi } from "lucide-react";
import { Switch } from "@/components/ui/switch";
import { usePlaylists } from "@/context/PlaylistsContext";
import { useAuth } from "@/context/AuthContext";
import { useAutoPins, setAutoFavorites, setAutoPlaylist, setWifiOnly, syncAuto, canDetectNetwork, onMobileData } from "@/lib/autoPins";
import { toast } from "sonner";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useOffline, getCapMB, setCapMB, CAP_OPTIONS, usedBytes, fmtBytes, fmtCap, clearAll, supported } from "@/lib/offline";

let deferredPrompt = null;
if (typeof window !== "undefined") {
  window.addEventListener("beforeinstallprompt", (e) => { e.preventDefault(); deferredPrompt = e; window.dispatchEvent(new Event("rh:installable")); });
}
const isStandalone = () => window.matchMedia("(display-mode: standalone)").matches || window.navigator.standalone === true;

export const InstallSection = () => {
  const [installable, setInstallable] = useState(!!deferredPrompt);
  const [installed, setInstalled] = useState(isStandalone());
  useEffect(() => {
    const on = () => setInstallable(true);
    const done = () => { setInstalled(true); setInstallable(false); };
    window.addEventListener("rh:installable", on); window.addEventListener("appinstalled", done);
    return () => { window.removeEventListener("rh:installable", on); window.removeEventListener("appinstalled", done); };
  }, []);
  const install = async () => {
    if (!deferredPrompt) return;
    deferredPrompt.prompt();
    const { outcome } = await deferredPrompt.userChoice;
    if (outcome === "accepted") toast.success("Resonance Harbor is being installed");
    deferredPrompt = null; setInstallable(false);
  };
  const ios = /iphone|ipad|ipod/i.test(navigator.userAgent) && !window.MSStream;
  return (
    <section className="mt-8 flex flex-wrap items-center justify-between gap-4 rounded-lg border border-zinc-200 bg-white p-8" data-testid="install-section">
      <div className="flex items-center gap-3">
        <div className="flex h-10 w-10 items-center justify-center rounded-md bg-sky-50 text-sky-700"><Smartphone size={18} /></div>
        <div>
          <h2 className="text-lg font-medium">Install as an app</h2>
          <p className="max-w-md text-xs text-zinc-500" data-testid="install-hint">
            {installed ? "You're using the installed app. Playback resumes where you left off." :
              ios ? "On iPhone/iPad: tap Share → “Add to Home Screen”." :
              installable ? "Adds a home-screen / desktop icon, opens full-screen and resumes where you left off." :
              "Open this site in Chrome, Edge or Safari and use the browser's “Install app” / “Add to Home Screen” option."}
          </p>
        </div>
      </div>
      {installed ? <span className="flex items-center gap-1.5 rounded-full bg-emerald-50 px-4 py-2 text-sm text-emerald-800" data-testid="install-done"><Check size={14} />Installed</span>
        : installable && <button onClick={install} data-testid="install-button" className="flex h-10 items-center gap-2 rounded-full bg-zinc-900 px-5 text-sm text-white hover:bg-zinc-700"><Download size={14} />Install</button>}
    </section>
  );
};

const AutoSync = () => {
  const { auto, status } = useAutoPins();
  const { playlists } = usePlaylists();
  const { user } = useAuth();
  const [open, setOpen] = useState(false);
  const chosen = playlists.filter((p) => auto.playlists.includes(p.id));
  return (
    <div className="mt-6 border-t border-zinc-200 pt-6" data-testid="auto-sync">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h3 className="text-sm font-medium">Always keep offline</h3>
          <p className="text-xs text-zinc-500">Re-synced automatically when you star songs or edit a chosen playlist, when you come back online, and every 15 minutes.</p>
        </div>
        <button onClick={() => syncAuto(user?.id, true)} disabled={status.syncing || (!auto.favorites && !auto.playlists.length)} data-testid="auto-sync-now"
          className="flex h-9 items-center gap-2 rounded-full border border-zinc-300 px-4 text-xs hover:border-zinc-900 disabled:opacity-40">
          {status.syncing ? <Loader2 size={12} className="animate-spin" /> : <RefreshCw size={12} />}{status.syncing ? "Syncing…" : "Sync now"}
        </button>
      </div>
      <label className="mt-4 flex items-center justify-between rounded-md border border-zinc-200 px-4 py-3" data-testid="auto-favorites-row">
        <span className="flex items-center gap-2 text-sm"><Heart size={14} className="text-amber-600" />Favorites</span>
        <Switch checked={auto.favorites} onCheckedChange={setAutoFavorites} data-testid="auto-favorites-switch" />
      </label>
      <div className="mt-2 rounded-md border border-zinc-200 px-4 py-3">
        <button onClick={() => setOpen((o) => !o)} data-testid="auto-playlists-toggle" className="flex w-full items-center justify-between text-sm">
          <span className="flex items-center gap-2"><ListMusic size={14} className="text-zinc-500" />Playlists<span className="font-mono text-[11px] text-zinc-500" data-testid="auto-playlists-count">{chosen.length ? `· ${chosen.map((p) => p.name).join(", ")}` : "· none chosen"}</span></span>
          <span className="text-xs text-zinc-500">{open ? "Done" : "Choose"}</span>
        </button>
        {open && (
          <div className="mt-3 max-h-56 space-y-1 overflow-y-auto" data-testid="auto-playlists-list">
            {playlists.length ? playlists.map((p) => (
              <label key={p.id} className="flex items-center justify-between rounded px-2 py-1.5 text-sm hover:bg-zinc-50">
                <span className="truncate">{p.name} <span className="font-mono text-[11px] text-zinc-400">{p.track_count} song{p.track_count === 1 ? "" : "s"}</span></span>
                <Switch checked={auto.playlists.includes(p.id)} onCheckedChange={(v) => setAutoPlaylist(p.id, v)} data-testid={`auto-playlist-switch-${p.id}`} />
              </label>
            )) : <p className="text-xs text-zinc-500">You have no playlists yet.</p>}
          </div>
        )}
      </div>
      <label className="mt-2 flex items-center justify-between rounded-md border border-zinc-200 px-4 py-3" data-testid="auto-wifi-row">
        <span>
          <span className="flex items-center gap-2 text-sm"><Wifi size={14} className="text-sky-600" />Only sync on Wi-Fi</span>
          <span className="block text-xs text-zinc-500" data-testid="auto-wifi-hint">
            {canDetectNetwork() ? "Automatic downloads pause on mobile data and resume on Wi-Fi. “Sync now” always runs." : "This browser can't tell Wi-Fi from mobile data, so automatic sync runs on any connection here. The setting applies on phones (Chrome/Edge on Android)."}
          </span>
        </span>
        <Switch checked={auto.wifiOnly} onCheckedChange={setWifiOnly} data-testid="auto-wifi-switch" />
      </label>
      <p className="mt-3 font-mono text-[11px] text-zinc-500" data-testid="auto-sync-status">
        {status.error ? <span className="text-red-600">Last sync failed: {status.error}</span>
          : status.waitingForWifi || (auto.wifiOnly && onMobileData() && (auto.favorites || auto.playlists.length)) ? <span className="text-amber-700">Waiting for Wi-Fi — you're on mobile data{status.last ? ` · last synced ${new Date(status.last).toLocaleString()}` : ""}</span>
          : status.last ? `Last synced ${new Date(status.last).toLocaleString()}` : auto.favorites || auto.playlists.length ? "Not synced yet" : ""}
      </p>
    </div>
  );
};

export const OfflineSection = () => {
  const { pins } = useOffline();
  const [cap, setCap] = useState(getCapMB());
  if (!supported()) return null;
  const used = usedBytes();
  const pct = Math.min(100, Math.round((used / (cap * 1024 * 1024)) * 100));
  const change = (v) => { setCapMB(Number(v)); setCap(Number(v)); toast.success(`Offline limit set to ${fmtCap(Number(v))} on this device`); };
  const clear = async () => {
    if (!window.confirm(`Remove all ${pins.length} saved albums/playlists from this device?`)) return;
    await clearAll(); toast.success("Offline music cleared");
  };
  return (
    <section className="mt-8 rounded-lg border border-zinc-200 bg-white p-8" data-testid="offline-section">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div className="flex items-center gap-3">
          <div className="flex h-10 w-10 items-center justify-center rounded-md bg-emerald-50 text-emerald-700"><CloudDownload size={18} /></div>
          <div>
            <h2 className="text-lg font-medium">Offline music</h2>
            <p className="text-xs text-zinc-500" data-testid="offline-section-usage">{pins.length} saved · {fmtBytes(used)} of {fmtCap(cap)} used on this device</p>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <Select value={String(cap)} onValueChange={change}>
            <SelectTrigger className="w-40" data-testid="offline-cap-select"><SelectValue /></SelectTrigger>
            <SelectContent>{CAP_OPTIONS.map((mb) => <SelectItem key={mb} value={String(mb)} data-testid={`offline-cap-${mb}`}>{fmtCap(mb)} limit</SelectItem>)}</SelectContent>
          </Select>
          <button onClick={clear} disabled={!pins.length} data-testid="offline-clear-button" className="flex h-10 items-center gap-2 rounded-full border border-red-200 px-4 text-sm text-red-600 hover:bg-red-600 hover:text-white disabled:opacity-40"><Trash2 size={14} />Clear all</button>
        </div>
      </div>
      <div className="mt-5 h-1.5 overflow-hidden rounded-full bg-zinc-100"><div className="h-full rounded-full bg-emerald-600" style={{ width: `${pct}%` }} data-testid="offline-cap-bar" /></div>
      <AutoSync />
    </section>
  );
};
